/**
 * wheelPropsEqual — the React.memo comparator for <WheelComponent>.
 *
 * Extracted VERBATIM from the inline `React.memo(WheelComponent, …)` comparator
 * (Nick: one behaviour per file + a matching test — this comparator was
 * previously un-unit-testable inline) and then given ONE targeted fix:
 *
 * ── THE FIX (EW-003, flag REACT_APP_SEGMENT_ARC_FILLS) ──────────────────────
 * The per-item comparison only inspected `id / value / color / selected /
 * position`. A **Segment Fill** change (Pattern / Gradient / Image / GIF) updates
 * the segment's fill MODEL (`settings.fill` + the legacy `bgWallpaper /
 * segmentBgColor / currentBg / isPattern` fields) but NOT `color` — so the
 * comparator returned `true` (props "equal") and React.memo SKIPPED the
 * re-render. The wheel WEDGE therefore never re-hydrated its fill on a live edit
 * in the Builder: `hydrateChartFill` (fed to the pie via `hydratedChartData`)
 * never recomputed, so the wedge kept its flat colour until a full page reload
 * remounted the component. (The View-Wheel page mounts fresh, so its wedge WAS
 * correct — this bug was isolated to the live-editing Builder. MEASURED live on
 * web-stg 2026-08-18, bundle main.56e25379.js.)
 *
 * The fix adds a wedge-fill comparison to the per-item loop, using the SAME
 * `resolveSegmentFill` resolver the renderer's `hydrateChartFill` uses — so the
 * memo re-renders exactly when the resolved wedge fill would change, never more.
 *
 * FLAG-GATED / byte-identical when OFF: the extra comparison only runs when
 * `SEGMENT_ARC_FILLS_ENABLED`. With the flag OFF the wedge never paints a fill
 * model anyway (`hydrateChartFill` is a pass-through), so skipping the compare is
 * correct AND keeps the comparator's behaviour identical to before this change.
 *
 * Note on JSON.stringify: the caller's own header warns it is "slow for large
 * objects" for the WHOLE props — that still holds; here we stringify only the
 * small, already-resolved fill MODEL of ONE item (null for a plain-colour
 * segment), and only when the flag is ON. Worst case a differing key order forces
 * one harmless extra redraw; it can never MISS a real change (unequal fills never
 * serialize equal), which is the only failure mode that matters for this bug.
 */

import { resolveSegmentFill } from "../SegmentBuilder/Shared/SegmentStyling/logic/fillModelFeed";
import { SEGMENT_ARC_FILLS_ENABLED } from "../D3Wheels/segmentArcFillsFlag";

// Serialised wedge-fill signature for a chart item (null when the segment models
// no wedge fill — a plain colour). Two items with the same signature paint the
// same wedge fill; a changed signature must force a re-render.
const fillSignature = (item) => {
  const fill = resolveSegmentFill(item);
  return fill ? JSON.stringify(fill) : "";
};

// ISP-9999 (segment-state desync / data loss): per-chart-item fields whose
// change MUST force a WheelComponent re-render, so the D3 pie's internal
// chartData re-syncs through the existing updateArcs(hydratedChartData) effect.
//
// Root cause this list fixes: the pie keeps its OWN internal chartData array
// and, on a wedge-selection click, emits that whole array back to redux
// (setWheelSegments). The pie only re-syncs that array via updateArcs when
// WheelComponent re-renders — but the comparator previously compared ONLY
// id/value/color/selected/position. A segment TITLE edit or a segment
// PHOTO/WALLPAPER delete changes NONE of those, so the component skipped
// rendering, the pie kept a STALE array, and the NEXT wedge click emitted that
// stale array back into redux — silently REVERTING the just-saved edit (title
// dropped, deleted photo reappears). Comparing the content keys below makes any
// content edit re-render, and a single re-render re-syncs the WHOLE hydrated
// item into the pie. Not flag-gated: this loss can happen with EW-003 arc-fills
// OFF, so the content compare must always run (the EW-003 fillSignature compare
// below stays flag-gated and is additive to this).
//
// NOTE: the resolved `icon` on a hydrated item is a React element (a fresh ref
// every render), so it is deliberately NOT compared here — comparing it would
// force a render on every parent update and defeat the memo. The underlying
// scalar content that co-changes with the reported loss vectors is covered.
export const WHEEL_CHART_ITEM_COMPARE_KEYS = [
  "id",
  "value",
  "color",
  "selected",
  "position",
  // Content keys (ISP-9999): label + fill/photo/wallpaper/pattern scalars.
  "title",
  "bgWallpaper",
  "currentBg",
  "patternId",
  "patternColor",
  "segmentBgColor",
  "wallpaperOverlayColor",
  "iconColor",
];

/**
 * @param {object} prevProps previous <WheelComponent> props
 * @param {object} nextProps next <WheelComponent> props
 * @returns {boolean} true when the props are equal (skip re-render)
 */
export const wheelPropsEqual = (prevProps, nextProps) => {
  // Don't use JSON.stringify for performance reasons - it's slow for large objects
  // Instead, do targeted comparisons on critical properties

  // Compare userData (name included so the alt text tracks the wheel name)
  if (
    prevProps.userData?.id !== nextProps.userData?.id ||
    prevProps.userData?.url !== nextProps.userData?.url ||
    prevProps.userData?.name !== nextProps.userData?.name
  ) {
    return false;
  }

  // WL-056b: re-render when the video center-media changes. When both are
  // undefined (the default / flag-OFF path) every comparison is false, so this
  // block is a no-op and the memo behaviour is byte-identical to today.
  if (
    prevProps.centerMedia?.type !== nextProps.centerMedia?.type ||
    prevProps.centerMedia?.source !== nextProps.centerMedia?.source ||
    prevProps.centerMedia?.poster !== nextProps.centerMedia?.poster
  ) {
    return false;
  }

  // Compare chart data length first (quick check)
  if (
    !prevProps.chartData ||
    !nextProps.chartData ||
    prevProps.chartData.length !== nextProps.chartData.length
  ) {
    return false;
  }

  // Then compare chart data items more efficiently
  for (let i = 0; i < prevProps.chartData.length; i++) {
    const prevItem = prevProps.chartData[i];
    const nextItem = nextProps.chartData[i];

    // ISP-9999: compare the identity + content scalar keys. Any change (title
    // edit, photo/wallpaper delete, pattern/fill scalar) forces a re-render so
    // the pie re-syncs and cannot later emit a stale array back into redux.
    for (let k = 0; k < WHEEL_CHART_ITEM_COMPARE_KEYS.length; k++) {
      const key = WHEEL_CHART_ITEM_COMPARE_KEYS[k];
      if (prevItem?.[key] !== nextItem?.[key]) {
        return false;
      }
    }

    // EW-003 wedge-fill live update: re-render when the Segment Fill MODEL
    // (Pattern / Gradient / Image / GIF) changes even though `color` did not.
    // Flag OFF → the wedge never paints a fill model, so this compare is skipped
    // (byte-identical to the pre-fix comparator).
    if (
      SEGMENT_ARC_FILLS_ENABLED &&
      fillSignature(prevItem) !== fillSignature(nextItem)
    ) {
      return false;
    }
  }

  // Compare the remaining scalar props
  return (
    prevProps.maxSelectedArcs === nextProps.maxSelectedArcs &&
    prevProps.showLabels === nextProps.showLabels &&
    prevProps.arcThickness === nextProps.arcThickness &&
    prevProps.margin === nextProps.margin &&
    prevProps.segmentHighlighting === nextProps.segmentHighlighting &&
    // ISP-9999 Discover live-ecosystem: `ambientActive` flips true/false on a
    // TIMER, on an otherwise-unchanged card (chartData/userData/etc. are all
    // still equal) — without this line the memo reports "equal" on every
    // ambient tick and WheelComponent never re-renders, so the ambient prop
    // change silently never reaches the wedge-hover-motion effect at all.
    prevProps.ambientActive === nextProps.ambientActive
  );
};

// ISP-9999 alias: PR #4525's test + call sites reference the comparator as
// `wheelComponentPropsAreEqual`. Same function, one source of truth (no
// duplicated comparator) — keeps both names resolvable after the EW-003
// extraction to this file.
export const wheelComponentPropsAreEqual = wheelPropsEqual;

export default wheelPropsEqual;
